import { motion } from "framer-motion";
import { ArrowUpRight, LineChart, ShoppingBag, ShieldCheck, Layers } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";
import { Reveal, Overline, MaskedLines } from "@/components/Motion";
import Seo from "@/components/Seo";

const FEATURES = [
  { icon: Layers, t: "Curated marketplace", d: "A discovery-led catalogue of investment products, hand-picked and organised for clarity." },
  { icon: LineChart, t: "Data-driven insights", d: "Compare, evaluate and understand products with transparent, research-backed information." },
  { icon: ShieldCheck, t: "Powered by MIDASX", d: "Access a trusted platform experience built for modern investors and advisors." },
];

export default function Products() {
  return (
    <div>
      <Seo
        title="Products | Arivu Finserve — MIDASX Investment Marketplace"
        description="Explore a curated range of investment products through Arivu Finserve's MIDASX discovery marketplace — built for transparency and confident decisions."
      />
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-brand-navy">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.texture} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/85 to-brand-green/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-32 w-full">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <Overline className="text-brand-sky">Products · Powered by MIDASX</Overline>
              </motion.div>
              <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] mt-6">
                <MaskedLines lines={["Explore our", "product"]} delay={0.3} />
                <span className="block italic text-brand-accent">
                  <MaskedLines lines={["marketplace."]} delay={0.55} />
                </span>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="text-white/70 text-lg font-light mt-8 max-w-xl leading-relaxed"
              >
                Discover a curated range of investment products through our dedicated MIDASX marketplace — built for transparency, comparison and confident decisions.
              </motion.p>
              <motion.a
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.15 }}
                href={SITE.midasx}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="midasx-hero-btn"
                className="inline-flex items-center gap-2 mt-10 rounded-full bg-brand-accent text-white text-base font-medium px-9 py-4 hover:bg-white hover:text-brand-navy transition-colors group"
              >
                Visit the marketplace <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </motion.a>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.94, rotate: -2 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 0.6, duration: 0.9 }}
                className="relative rounded-3xl overflow-hidden border border-white/15"
              >
                <img src={IMAGES.growth} alt="MIDASX marketplace" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8">
                  <div className="flex items-center gap-2 text-white">
                    <ShoppingBag className="h-5 w-5 text-brand-accent" />
                    <span className="font-serif text-2xl">Discovery Marketplace</span>
                  </div>
                  <p className="text-white/60 text-sm mt-2">arivufinserve.midasx.in</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="products-features" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <Reveal>
          <Overline>Why the marketplace</Overline>
          <h2 className="font-serif text-4xl lg:text-6xl text-brand-navy font-light mt-5 max-w-3xl leading-tight">
            A smarter way to discover investment products.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8 mt-14">
          {FEATURES.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.1}>
              <div className="bg-white border border-brand-border rounded-2xl p-8 h-full hover:border-brand-green transition-colors">
                <span className="inline-flex h-12 w-12 rounded-xl bg-brand-green/10 items-center justify-center">
                  <f.icon className="h-6 w-6 text-brand-green" />
                </span>
                <h3 className="font-serif text-2xl text-brand-navy mt-6">{f.t}</h3>
                <p className="text-brand-muted font-light mt-3 leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 bg-brand-navy rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8 relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="font-serif text-white text-3xl lg:text-4xl font-light">Ready to explore your options?</h3>
              <p className="text-white/60 font-light mt-3">Head to the MIDASX discovery marketplace to browse products.</p>
            </div>
            <a
              href={SITE.midasx}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="midasx-cta-btn"
              className="relative z-10 flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-brand-navy text-sm font-medium px-8 py-4 hover:bg-brand-accent hover:text-white transition-colors group"
            >
              Open marketplace <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
